import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useCan } from '../../contexts/PermissionsContext'
import { type ThematicView, useThematicCatalogue, useThematicLegend } from './api'
import { buildColourIndex } from './colour'
import { ThematicLegend } from './ThematicLegend'
import { ThematicPicker } from './ThematicPicker'
import { useThematicChoice } from './useThematicChoice'

/**
 * The state of one "Colour by" bar: the choice (URL + local storage), the
 * catalogue and legend it loads, and the element-uid -> colour index a view
 * draws with.
 */
export function useThematic(org: string, assetUid: string | undefined, view: ThematicView, defaultKey: string) {
  const choice = useThematicChoice(view, defaultKey)
  const catalogue = useThematicCatalogue(org, assetUid, view)
  const legend = useThematicLegend(org, assetUid, view, choice.key)
  const index = useMemo(() => buildColourIndex(legend.data), [legend.data])

  return { choice, catalogue, legend, index }
}

/** The picker and the legend, side by side. */
export function ThematicBar({
  org, state,
}: {
  org: string
  state: ReturnType<typeof useThematic>
}) {
  const { t } = useTranslation('thematics')
  const canEditColours = useCan('configuration.write')
  const { choice, catalogue, legend } = state
  const propertyUid = legend.data?.property_uid
  const editHref = canEditColours && propertyUid && legend.data?.data_type === 'enum'
    ? `/${org}/properties/${propertyUid}/edit`
    : undefined

  const key = legend.data?.key ?? ''
  const editDefinitionHref = canEditColours && key.startsWith('definition:')
    ? `/${org}/types/thematics/${encodeURIComponent(key.slice('definition:'.length))}`
    : undefined

  return (
    <div data-testid="thematic-bar" className="flex flex-wrap items-center gap-3">
      <ThematicPicker
        catalogue={catalogue.data}
        value={choice.key}
        onChange={(key, source, coverage) => choice.setKey(key, source, coverage)}
      />
      {choice.key && legend.isError && (
        <span className="text-xs text-muted-foreground" data-testid="thematic-legend-error">{t('unavailable')}</span>
      )}
      {choice.key && legend.data && (
        <ThematicLegend
          legend={legend.data}
          isolated={choice.isolated}
          onToggleIsolated={choice.toggleIsolated}
          editColoursHref={editHref}
          editDefinitionHref={editDefinitionHref}
        />
      )}
    </div>
  )
}
